import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Bug, Check, ChevronLeft, FileText, Loader2, Play, Sparkles, Square, X,
} from 'lucide-react';
import { env } from '@/env';
import { cn } from '@/lib/utils';
import { isNative, tokenSync } from '@/lib/native-auth';
import { audioUrl, fotoAudioUrl } from '../../api';
import { OfflineMap, type FotoPonto } from '@/modules/agronomia/components/OfflineMap';
import {
  useVisita,
  usePatchFoto,
  usePatchAudio,
  usePutOcorrencias,
  useFinalizarVisita,
  useResumoIA,
  useSaveResumo,
  visitaRelatorioUrl,
  type OcorrenciaInput,
} from '../api';
import type { VisitaDetail } from '../schemas';

type Talhao = VisitaDetail['talhoes'][number];
type Foto = Talhao['fotos'][number];

const textareaCls =
  'w-full rounded-[12px] border border-[var(--agro-card-border)] bg-surface px-3 py-2.5 text-[0.85rem] ' +
  'text-text placeholder:text-text-muted resize-y focus:outline-none focus:border-[var(--accent)] ' +
  'focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)]';

const TIPO_LABEL: Record<string, string> = {
  doenca: 'Doença', praga: 'Praga', daninha: 'Daninha', nutricao: 'Nutrição', alerta: 'Alerta',
};

// <img> que no nativo busca com Bearer e vira object URL (img tag não manda header).
function AuthImg({ url, className, onClick }: { url: string; className?: string; onClick?: () => void }) {
  const [src, setSrc] = useState<string | undefined>(isNative() ? undefined : url);
  useEffect(() => {
    if (!isNative()) { setSrc(url); return; }
    let objectUrl: string | null = null;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`${env.VITE_API_BASE}${url}`, {
          headers: tokenSync() ? { Authorization: `Bearer ${tokenSync()}` } : {},
        });
        const blob = await res.blob();
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch { /* ignora */ }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);
  return <img src={src} alt="" className={className} onClick={onClick} />;
}

// Player estilo mensagem de voz (tela 11b): círculo coral play/pause + barras
// coral + duração à direita.
const WAVE_BARS = [10, 15, 18, 12, 19, 13, 11, 16, 12, 18, 12, 15, 13, 17, 12, 15, 11, 16, 13, 18];
function WaveAudio({ src }: { src: string }) {
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState<number | null>(null);
  const [pos, setPos] = useState(0);
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  return (
    <div className="flex w-full items-center gap-[9px] rounded-full py-[7px] pl-[7px] pr-3">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d)) setDur(d); }}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onEnded={() => { setPlaying(false); setPos(0); }}
      />
      <button
        type="button"
        aria-label={playing ? 'Pausar' : 'Tocar'}
        onClick={() => {
          const a = audioRef.current;
          if (!a) return;
          if (playing) { a.pause(); setPlaying(false); }
          else { void a.play(); setPlaying(true); }
        }}
        className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-[#e96a45] text-white active:scale-95"
      >
        {playing ? <Square size={11} strokeWidth={2.5} fill="currentColor" /> : <Play size={13} strokeWidth={2.5} fill="currentColor" className="ml-0.5" />}
      </button>
      <div className="flex min-w-0 flex-1 items-center justify-between overflow-hidden">
        {WAVE_BARS.map((h, i) => {
          const done = dur ? (pos / dur) * WAVE_BARS.length > i : false;
          return (
            <span
              key={i}
              className={cn('w-[2.5px] shrink-0 rounded-[2px] bg-[#e96a45]', done ? '' : playing ? 'opacity-40' : '')}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>
      <span className="shrink-0 text-[11px] font-medium text-[#565350]">
        {playing ? fmt(pos) : dur != null ? fmt(dur) : '0:00'}
      </span>
    </div>
  );
}

function agruparPontos(fotos: Foto[]): Foto[][] {
  const map = new Map<string, Foto[]>();
  const ordem: string[] = [];
  for (const f of fotos) {
    const k = f.ponto_uuid || `f${f.id}`;
    if (!map.has(k)) { map.set(k, []); ordem.push(k); }
    map.get(k)!.push(f);
  }
  return ordem.map((k) => map.get(k)!);
}

// Áudios de uma foto: a lista nova (relatorio_foto_audio) e, pra fotos antigas,
// o áudio único das colunas legadas.
function audiosDaFoto(f: Foto): { key: string; src: string; status: string | null; transcricao: string; audioId: number | null }[] {
  if (f.audios && f.audios.length) {
    return f.audios.map((a) => ({
      key: `a${a.id}`, src: fotoAudioUrl(a.id), status: a.status, transcricao: a.transcricao ?? '', audioId: a.id,
    }));
  }
  if (f.audio_url) {
    return [{ key: `f${f.id}`, src: audioUrl(f.id), status: f.audio_status ?? null, transcricao: f.audio_transcricao ?? '', audioId: null }];
  }
  return [];
}

// Um ponto de visita = registros com o mesmo ponto_uuid. Com mais de uma foto o
// card vira carrossel (setas); ponto marcado sem foto aparece sem imagem.
// Ordem: etiquetas → ocorrências → anotações → áudios (com transcrição).
function PontoCard({
  fotos,
  visitaId,
  talhao,
  indice,
  temPraga,
}: {
  fotos: Foto[];
  visitaId: number;
  talhao: Talhao;
  indice: number;
  temPraga: boolean;
}) {
  const talhaoRotulo = talhao.talhao_nome || `Talhão ${talhao.talhao_numero}`;
  const estadio = talhao.estadio_fenologico ?? null;
  const patch = usePatchFoto(visitaId);
  const patchAudio = usePatchAudio(visitaId);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [salvo, setSalvo] = useState(false);
  // Estado POR FOTO: anotações e as transcrições dos áudios daquela foto.
  const [recs, setRecs] = useState<string[]>(() => fotos.map((f) => f.recomendacao ?? ''));
  const [transcs, setTranscs] = useState<string[][]>(() => fotos.map((f) => audiosDaFoto(f).map((a) => a.transcricao)));

  const ai = Math.min(active, fotos.length - 1);
  const foto = fotos[ai]!;
  const audios = audiosDaFoto(foto);
  const comFoto = foto.url != null;
  const passo = (d: number) => { setActive((v) => (Math.min(v, fotos.length - 1) + d + fotos.length) % fotos.length); setSalvo(false); };
  const setRec = (v: string) => { setSalvo(false); setRecs((prev) => prev.map((x, i) => (i === ai ? v : x))); };
  const setTransc = (k: number, v: string) =>
    setTranscs((prev) => prev.map((arr, i) => (i === ai ? arr.map((x, j) => (j === k ? v : x)) : arr)));

  async function salvar() {
    for (let i = 0; i < fotos.length; i++) {
      const f = fotos[i]!;
      const lista = audiosDaFoto(f);
      await patch.mutateAsync({
        fotoId: f.id,
        body: {
          recomendacao: recs[i] ?? '',
          // Foto sem lista nova (legado): a transcrição ainda mora na foto.
          ...(lista.length && lista[0]!.audioId == null
            ? { recomendacao_audio_transcricao: transcs[i]?.[0] ?? '' }
            : {}),
        },
      });
      for (let k = 0; k < lista.length; k++) {
        const a = lista[k]!;
        if (a.audioId == null) continue;
        const texto = transcs[i]?.[k] ?? '';
        if (texto !== a.transcricao) await patchAudio.mutateAsync({ audioId: a.audioId, transcricao: texto });
      }
    }
    setSalvo(true);
  }

  return (
    <div className="overflow-hidden rounded-[12px] border border-[#d8d5d0] bg-[#f1f1ef]">
      {comFoto && (
        <div className="relative">
          {/* imagem inteira, na proporção da captura — sem corte */}
          <AuthImg url={foto.url!} className="block max-h-[380px] w-full object-contain" onClick={() => setZoom(true)} />
          {fotos.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Anterior"
                onClick={() => passo(-1)}
                className="absolute left-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-[1.3rem] leading-none text-white"
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Próxima"
                onClick={() => passo(1)}
                className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-[1.3rem] leading-none text-white"
              >
                ›
              </button>
              <span className="absolute right-2.5 top-2.5 rounded-full bg-[rgba(17,20,15,0.6)] px-[9px] py-1 text-[11px] font-medium text-white">
                {ai + 1} / {fotos.length}
              </span>
            </>
          )}
        </div>
      )}

      <div className="flex flex-col gap-[9px] px-3.5 pb-[13px] pt-3">
        {/* etiquetas: Ponto · Talhão · Estádio */}
        <div className="flex flex-wrap items-center gap-[6px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-[#fde3d5] px-2.5 py-[3px] text-[12px] font-medium leading-4 text-[#611f0e]">
            Ponto {indice}
            {temPraga && <Bug size={12} strokeWidth={2.25} aria-label="apontamento de praga" />}
          </span>
          <span className="rounded-full bg-[#eae7e3] px-2.5 py-[3px] text-[12px] font-medium leading-4 text-[#3a3530]">{talhaoRotulo}</span>
          {estadio && (
            <span className="rounded-full bg-[#eae7e3] px-2.5 py-[3px] text-[12px] font-medium leading-4 text-[#3a3530]">Estádio {estadio}</span>
          )}
          {comFoto
            ? fotos.length > 1 && (
              <span className="rounded-full bg-[#eae7e3] px-2.5 py-[3px] text-[12px] font-medium leading-4 text-[#3a3530]">{fotos.length} fotos</span>
            )
            : <span className="rounded-full bg-[#eae7e3] px-2.5 py-[3px] text-[12px] font-medium leading-4 text-[#3a3530]">Sem foto</span>}
        </div>

        {/* OCORRÊNCIAS CONFIRMADAS — dados do talhão */}
        <p className="text-[11px] font-medium tracking-[0.5px] text-[#565350]">OCORRÊNCIAS CONFIRMADAS</p>
        <OcorrenciasInline talhao={talhao} visitaId={visitaId} />

        {/* ANOTAÇÕES */}
        <p className="text-[11px] font-medium tracking-[0.5px] text-[#565350]">
          ANOTAÇÕES{comFoto && fotos.length > 1 ? ` · FOTO ${ai + 1}/${fotos.length}` : ''}
        </p>
        <textarea
          className="w-full resize-none rounded-[12px] border-[1.4px] border-[#727970] bg-transparent px-3 py-2.5 text-[14px] leading-[20px] text-[#1a1a19] placeholder:text-text-muted focus:border-[var(--accent)] focus:outline-none"
          rows={3}
          value={recs[ai] ?? ''}
          onChange={(e) => setRec(e.target.value)}
          placeholder="Anotações do agrônomo para este ponto…"
        />

        {/* ÁUDIOS + TRANSCRIÇÃO — um player por gravação */}
        {audios.length > 0 && (
          <>
            <p className="text-[11px] font-medium tracking-[0.5px] text-[#565350]">
              {audios.length > 1 ? `${audios.length} ÁUDIOS` : 'ÁUDIO'} + TRANSCRIÇÃO
            </p>
            {audios.map((a, k) => (
              <div key={a.key} className="flex flex-col gap-1">
                {audios.length > 1 && (
                  <span className="text-[11px] text-text-muted">
                    Áudio {k + 1}
                    {a.status === 'pendente' ? ' · transcrevendo…' : a.status === 'erro' ? ' · falha' : ''}
                  </span>
                )}
                <WaveAudio src={a.src} />
                <textarea
                  className="w-full resize-none rounded-[10px] border border-[#e6e4e0] bg-[#fbfbfa] px-[11px] py-[9px] text-[12px] italic leading-[17px] text-[#565350] placeholder:text-text-muted focus:border-[var(--accent)] focus:outline-none"
                  rows={2}
                  value={transcs[ai]?.[k] ?? ''}
                  onChange={(e) => setTransc(k, e.target.value)}
                  placeholder="Transcrição do áudio (edite se a IA errou)…"
                />
              </div>
            ))}
          </>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            disabled={patch.isPending || patchAudio.isPending}
            onClick={() => void salvar()}
            className="inline-flex items-center gap-1.5 rounded-full bg-surface3 px-4 py-2 text-[0.82rem] font-semibold text-text active:scale-95 disabled:opacity-50"
          >
            {(patch.isPending || patchAudio.isPending) ? <Loader2 size={14} strokeWidth={2} className="animate-spin" /> : <Check size={14} strokeWidth={2} />}
            {salvo ? 'Ponto salvo' : 'Salvar ponto'}
          </button>
        </div>
      </div>

      {zoom && comFoto && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/95" onClick={() => setZoom(false)}>
          <button type="button" aria-label="Fechar" className="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-white/15 text-white">
            <X size={22} strokeWidth={2} />
          </button>
          <div className="flex min-h-0 flex-1 items-center justify-center p-3">
            <AuthImg url={foto.url!} className="max-h-full max-w-full object-contain" />
          </div>
          {/* anotações + transcrições acompanham a foto expandida */}
          <div
            className="max-h-[45vh] shrink-0 overflow-y-auto rounded-t-[18px] bg-surface px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3.5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-surface3" />
            {(recs[ai] ?? '').trim() && (
              <div className="mb-3">
                <div className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-[#1d5a33]">Anotações</div>
                <p className="text-[0.85rem] leading-snug text-text">{recs[ai]}</p>
              </div>
            )}
            {(transcs[ai] ?? []).filter((t) => t.trim()).map((t, k) => (
              <div key={k} className="mb-3">
                <div className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-text-muted">Áudio transcrito {k + 1}</div>
                <p className="text-[0.85rem] leading-snug text-text-2">{t}</p>
              </div>
            ))}
            {!(recs[ai] ?? '').trim() && !(transcs[ai] ?? []).some((t) => t.trim()) && (
              <p className="pb-2 text-center text-[0.82rem] text-text-muted">Sem anotação ou áudio nesta foto ainda.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Ocorrências do talhão DENTRO do card: etiquetas + "+ ocorrência", salvando a
// cada mudança (sem botão). Os apontamentos de praga vindos do campo chegam aqui
// um por ponto, com a quantidade contada — é o que vira média no relatório, por
// isso a lista devolve quantidade e ponto_uuid intactos no PUT.
function OcorrenciasInline({ talhao, visitaId }: { talhao: Talhao; visitaId: number }) {
  const put = usePutOcorrencias(visitaId);
  const [itens, setItens] = useState<OcorrenciaInput[]>(() =>
    talhao.ocorrencias.map((o) => ({
      tipo: o.tipo, nome: o.nome, severidade: o.severidade, quantidade: o.quantidade,
      unidade: o.unidade, observacao: o.observacao, ponto_uuid: o.ponto_uuid ?? null,
    })),
  );
  const [novoOpen, setNovoOpen] = useState(false);
  const [novoTipo, setNovoTipo] = useState('praga');
  const [novoNome, setNovoNome] = useState('');
  const [novaSev, setNovaSev] = useState('');
  const [novaQtd, setNovaQtd] = useState('');

  function persist(next: OcorrenciaInput[]) {
    setItens(next);
    put.mutate({ relatorioId: talhao.id, ocorrencias: next.filter((x) => (x.nome ?? '').trim()) });
  }
  const add = (o: OcorrenciaInput) => persist([...itens, o]);
  const rm = (i: number) => persist(itens.filter((_, idx) => idx !== i));
  function addNovo() {
    const nome = novoNome.trim();
    if (!nome) return;
    const q = novaQtd.trim() ? Number(novaQtd.replace(',', '.')) : null;
    add({ tipo: novoTipo, nome, severidade: novaSev || null, quantidade: q != null && !Number.isNaN(q) ? q : null });
    setNovoNome('');
    setNovaSev('');
    setNovaQtd('');
    setNovoOpen(false);
  }

  const ocorTone = (s2: string | null | undefined) =>
    s2 === 'critica' || s2 === 'alta' ? 'bg-[#fde3d5] text-[#8a2c14]'
      : s2 === 'media' ? 'bg-[#f7e6c9] text-[#6a4a17]'
        : 'bg-[#dcefdf] text-[#1d5a33]';
  const selCls = 'h-9 rounded-[10px] border border-[var(--agro-card-border)] bg-surface px-2 text-[0.8rem] text-text focus:outline-none focus:border-[var(--accent)]';

  return (
    <div className="flex flex-col gap-[9px]">
      <div className="flex flex-wrap items-center gap-[6px]">
        {itens.filter((it) => (it.nome ?? '').trim()).map((it, i) => (
          <span key={i} className={cn('inline-flex items-center gap-1.5 rounded-full py-[3px] pl-2.5 pr-2 text-[12px] font-medium leading-4', ocorTone(it.severidade))}>
            {TIPO_LABEL[it.tipo] ?? it.tipo} · {it.nome}
            {it.quantidade != null ? ` · ${it.quantidade}` : ''}
            {it.severidade ? ` · ${it.severidade}` : ''}
            <button type="button" aria-label="Remover" onClick={() => rm(i)} className="active:scale-90">
              <X size={11} strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setNovoOpen((v) => !v)}
          className="rounded-full border border-dashed border-[#bdb9b4] py-[3px] pl-2.5 pr-3 text-[12px] font-medium leading-4 text-[#565350] active:scale-95"
        >
          + ocorrência
        </button>
        {put.isPending && <Loader2 size={13} strokeWidth={2} className="animate-spin text-text-muted" />}
      </div>
      {novoOpen && (
        <div className="flex flex-wrap items-center gap-2">
          <select value={novoTipo} onChange={(e) => setNovoTipo(e.target.value)} className={cn(selCls, 'w-[100px]')}>
            {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            placeholder="Nome (ex: percevejos)"
            className="h-9 min-w-[120px] flex-1 rounded-[10px] border border-[var(--agro-card-border)] bg-surface px-2.5 text-[0.8rem] text-text placeholder:text-text-muted focus:outline-none focus:border-[var(--accent)]"
          />
          <input
            value={novaQtd}
            onChange={(e) => setNovaQtd(e.target.value)}
            inputMode="decimal"
            placeholder="qtd"
            className="h-9 w-[70px] rounded-[10px] border border-[var(--agro-card-border)] bg-surface px-2.5 text-[0.8rem] text-text placeholder:text-text-muted focus:outline-none focus:border-[var(--accent)]"
          />
          <select value={novaSev} onChange={(e) => setNovaSev(e.target.value)} className={cn(selCls, 'w-[92px]')}>
            <option value="">sev.</option>
            <option value="baixa">baixa</option>
            <option value="media">média</option>
            <option value="alta">alta</option>
            <option value="critica">crítica</option>
          </select>
          <button type="button" onClick={addNovo} className="rounded-full bg-surface3 px-3 py-2 text-[0.78rem] font-semibold text-text active:scale-95">
            OK
          </button>
        </div>
      )}
    </div>
  );
}

// Contagem de pragas do talhão: o agrônomo aponta a quantidade em vários pontos
// e aqui vira total + média por ponto — o mesmo bloco que sai no relatório.
function PragasResumo({ talhao }: { talhao: Talhao }) {
  const linhas = useMemo(() => {
    const m = new Map<string, { nome: string; n: number; total: number }>();
    for (const o of talhao.ocorrencias) {
      if (o.tipo !== 'praga' || o.quantidade == null) continue;
      const nome = (o.nome ?? '—').trim();
      const k = nome.toLowerCase();
      if (!m.has(k)) m.set(k, { nome, n: 0, total: 0 });
      const g = m.get(k)!;
      g.n++;
      g.total += o.quantidade;
    }
    return [...m.values()];
  }, [talhao.ocorrencias]);

  if (!linhas.length) return null;
  const num = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ','));
  return (
    <div className="overflow-hidden rounded-[10px] border border-[#e6e4e0] bg-[#fbfbfa]">
      <p className="px-3 pt-2.5 text-[11px] font-medium tracking-[0.5px] text-[#565350]">PRAGAS · MÉDIA POR PONTO</p>
      <div className="flex flex-col">
        {linhas.map((g) => (
          <div key={g.nome} className="flex items-center gap-2 px-3 py-2.5">
            <span className="min-w-0 flex-1 truncate text-[14px] leading-5 text-[#1a1a19]">{g.nome}</span>
            <span className="text-[12px] font-medium tabular-nums text-[#1a1a19]">{num(g.total / g.n)}/ponto</span>
            <span className="text-[11px] tabular-nums text-text-muted">
              {g.n} {g.n === 1 ? 'ponto' : 'pontos'} · total {num(g.total)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Resumo executivo do relatório, gerado pela IA e revisado pelo agrônomo.
function ResumoIACallout({ visitaId, current }: { visitaId: number; current: string | null }) {
  const gen = useResumoIA(visitaId);
  const save = useSaveResumo(visitaId);
  const [text, setText] = useState(current ?? '');
  return (
    <div className="rounded-[14px] border border-[var(--agro-card-border)] bg-[var(--accent-100)] p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Sparkles size={16} strokeWidth={1.75} className="text-accent" />
          <h3 className="text-[0.9rem] font-bold text-text">Resumo com IA</h3>
        </div>
        <button
          type="button"
          disabled={gen.isPending}
          onClick={() => gen.mutate(undefined, { onSuccess: (d) => setText(d.resumo) })}
          className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-[0.78rem] font-semibold text-accent active:scale-95 disabled:opacity-50"
        >
          {gen.isPending ? <Loader2 size={13} strokeWidth={2} className="animate-spin" /> : <Sparkles size={13} strokeWidth={2} />}
          {text ? 'Regerar' : 'Gerar'}
        </button>
      </div>
      <p className="mt-1 text-[0.76rem] text-text-2">Gere o panorama geral depois de revisar os pontos; ajuste o texto e salve.</p>
      <textarea
        className={cn(textareaCls, 'mt-2 bg-surface')}
        rows={5}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Diagnóstico geral da visita…"
      />
      {gen.isError && <p className="mt-1.5 text-[0.76rem] text-[#b3261e]">Falha ao gerar: {gen.error?.message ?? 'erro'}</p>}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          disabled={save.isPending || !text.trim()}
          onClick={() => save.mutate(text.trim())}
          className="inline-flex items-center gap-1.5 rounded-full bg-surface3 px-4 py-2 text-[0.82rem] font-semibold text-text active:scale-95 disabled:opacity-50"
        >
          {save.isPending ? <Loader2 size={13} strokeWidth={2} className="animate-spin" /> : <Check size={13} strokeWidth={2} />}
          {save.isSuccess ? 'Resumo salvo' : 'Salvar resumo'}
        </button>
      </div>
    </div>
  );
}

export function VisitaRelatorio({ id }: { id: number }) {
  const navigate = useNavigate();
  const { data: visita, isLoading, isError } = useVisita(id);
  const finalizar = useFinalizarVisita(id);

  // Pontos onde as fotos foram tiradas → marcadores no mapa do topo. A
  // numeração REINICIA a cada talhão (talhão 1: 1..n, talhão 2: 1..n de novo) —
  // é a mesma numeração dos cards e dos pinos do relatório.
  const fotoPontos = useMemo<FotoPonto[]>(() => {
    const out: FotoPonto[] = [];
    for (const t of visita?.talhoes ?? []) {
      let n = 0;
      for (const grupo of agruparPontos(t.fotos)) {
        const comGps = grupo.find((f) => f.lat != null && f.lon != null);
        n++;
        if (comGps) {
          out.push({
            lat: comGps.lat!,
            lon: comGps.lon!,
            label: String(n),
            titulo: `Ponto ${n} · ${t.talhao_nome || `Talhão ${t.talhao_numero}`}${grupo.length > 1 ? ` · ${grupo.length} fotos` : ''}`,
          });
        }
      }
    }
    return out;
  }, [visita]);

  if (isLoading) return <p className="px-4 py-8 text-center text-sm text-text-muted">Carregando visita…</p>;
  if (isError || !visita) return <p className="px-4 py-8 text-center text-sm text-[#b3261e]">Visita não encontrada.</p>;

  const revisada = visita.status === 'revisado';
  const dataFmt = (() => {
    try { return new Date(visita.data_visita).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return ''; }
  })();

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex flex-col px-3 pt-4 pb-3 shrink-0">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">Revisar visita</h1>
          {revisada && (
            <span className="shrink-0 rounded-full bg-[#dcefdf] px-2.5 py-1 text-[0.72rem] font-semibold text-[#1d5a33]">Revisada</span>
          )}
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2">
          {[visita.fazenda ?? 'Fazenda', dataFmt].filter(Boolean).join(' · ')}
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-28">
        <div className="flex flex-col gap-3.5">
          {/* mapa da fazenda com os pontos onde as fotos foram tiradas */}
          {visita.imovel_id != null && (
            <div className="h-[300px] overflow-hidden rounded-2xl bg-surface2">
              <OfflineMap imovelId={visita.imovel_id} fotoPontos={fotoPontos} className="h-full w-full object-cover" />
            </div>
          )}

          <ResumoIACallout visitaId={id} current={visita.resumo_ia ?? null} />

          {visita.observacoes_gerais && (
            <div className="rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 p-3 text-[0.84rem] leading-snug text-text-2">
              {visita.observacoes_gerais}
            </div>
          )}

          {/* talhões → pontos */}
          {visita.talhoes.map((t) => {
            const pontos = agruparPontos(t.fotos);
            // Pontos com apontamento de praga — ganham o ícone de inseto junto
            // do número, igual ao pino do mapa e ao card do relatório.
            const pontosComPraga = new Set(
              t.ocorrencias.filter((o) => o.tipo === 'praga' && o.ponto_uuid).map((o) => o.ponto_uuid!),
            );
            return (
              <section key={t.id} className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2 pt-1">
                  <h2 className="text-[0.95rem] font-bold text-text">{t.talhao_nome || `Talhão ${t.talhao_numero}`}</h2>
                  {t.estadio_fenologico && (
                    <span className="rounded-full bg-[var(--accent-100)] px-2 py-0.5 text-[0.72rem] font-medium text-accent">
                      Estádio {t.estadio_fenologico}
                    </span>
                  )}
                  {t.variedade && (
                    <span className="rounded-full bg-surface3 px-2 py-0.5 text-[0.72rem] font-medium text-text-2">{t.variedade}</span>
                  )}
                </div>
                {pontos.length === 0 && <p className="text-[0.8rem] text-text-muted">Sem pontos.</p>}
                <PragasResumo talhao={t} />
                {pontos.map((fotos, i) => (
                  <PontoCard
                    key={fotos[0]!.id}
                    fotos={fotos}
                    visitaId={id}
                    talhao={t}
                    indice={i + 1}
                    temPraga={pontosComPraga.has(fotos[0]!.ponto_uuid ?? '')}
                  />
                ))}
              </section>
            );
          })}

          <a href={visitaRelatorioUrl(id)} target="_blank" rel="noreferrer" className="block pt-1">
            <span className="flex w-full items-center justify-center gap-2 rounded-full border-[1.4px] border-[#bdb9b4] py-3 text-[0.86rem] font-medium text-text">
              <FileText size={17} strokeWidth={1.75} /> Pré-visualizar relatório
            </span>
          </a>
        </div>
      </div>

      {/* CTA: salvar revisão → relatório nasce */}
      <div className="absolute inset-x-0 bottom-0 border-t border-[var(--agro-card-border)] bg-bg/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
        <button
          type="button"
          disabled={finalizar.isPending || revisada}
          onClick={() =>
            finalizar.mutate(undefined, {
              onSuccess: () => {
                // Revisão concluída → volta pra tela inicial da fazenda.
                if (visita.imovel_id != null) {
                  void navigate({ to: '/agronomia/fazendas/$id', params: { id: String(visita.imovel_id) } });
                } else {
                  window.history.back();
                }
              },
            })
          }
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] active:scale-[0.99] disabled:opacity-60"
        >
          {finalizar.isPending ? <Loader2 size={18} strokeWidth={2} className="animate-spin" /> : <Check size={18} strokeWidth={2.25} />}
          {revisada ? 'Relatório gerado' : 'Salvar e gerar relatório'}
        </button>
      </div>
    </div>
  );
}
